import { useEffect, useState } from 'react';
import { Button, Offcanvas, Tab, Tabs } from 'react-bootstrap';
import ESpinner from 'components/others/Spinner';
import RequestStatusBadge from '../RequestStatusBadge';
import useSupplierRequestComing from '../model/useSupplierRequestComing';
import useViewSupplierRequest from '../model/useViewSupplierRequest';
import TabComing from './tabs/TabComing';
import TabRequest from './tabs/TabRequest';

interface OffViewProps {
    requestId: number | null;
    canOps: boolean;
    onClose: () => void;
    onChanged: () => void;
}

type ViewTabKey = 'request' | 'coming';

const OffView = ({ requestId, canOps, onClose, onChanged }: OffViewProps) => {
    const show = requestId != null;
    const [activeTab, setActiveTab] = useState<ViewTabKey>('request');
    const {
        t,
        request,
        setRequest,
        items,
        isLoading,
        isActive,
        hasRemaining,
        handleHide,
    } = useViewSupplierRequest({ requestId, onClose });

    const {
        canCredit,
        canStartComing,
        showDraft,
        draftComing,
        setDraftComing,
        isSubmitting,
        pendingCloudFiles,
        setPendingCloudFiles,
        cloudProgress,
        getMaxCount,
        handleOpenDraft,
        handleCancelDraft,
        handleConduct,
    } = useSupplierRequestComing({
        requestId,
        items,
        canOps,
        isActive,
        hasRemaining,
        requestDt: request?.dt ?? null,
        onSuccess: (next) => setRequest(next),
        onListChanged: onChanged,
    });

    useEffect(() => {
        if (!show) setActiveTab('request');
    }, [show, requestId]);

    const vendorLabel = request?.vendorName || `#${request?.vendorId || ''}`;
    const fileUrl = String(request?.filePublicUrl || '').trim();
    const note = String(request?.note || '').trim();
    const vendorNote = String(request?.vendorNote || '').trim();

    const onHide = () => {
        handleCancelDraft();
        handleHide();
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="off-import">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="d-flex align-items-center gap-2 flex-wrap">
                    <span>{t('supplierRequests.view.title', { vendor: vendorLabel })}</span>
                    {request ? <RequestStatusBadge status={Number(request.status)} /> : null}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {isLoading || !request ? (
                    <ESpinner />
                ) : (
                    <div className="off-coming-form d-flex flex-column h-100">
                        <div className="off-coming-content px-3">
                            <Tabs
                                variant="underline"
                                id="off-supplier-request-tab"
                                className="mb-3 invoice-tab border-bottom"
                                activeKey={activeTab}
                                onSelect={(key) => setActiveTab((key as ViewTabKey) || 'request')}
                            >
                                <Tab
                                    eventKey="request"
                                    title={
                                        <>
                                            <i className="bi bi-card-list me-2 fs-14"></i>
                                            {t('supplierRequests.view.tabRequest')}
                                        </>
                                    }
                                >
                                    <TabRequest
                                        items={items}
                                        note={note}
                                        vendorNote={vendorNote}
                                        fileUrl={fileUrl}
                                    />
                                </Tab>
                                <Tab
                                    eventKey="coming"
                                    title={
                                        <>
                                            <i className="bi bi-box-arrow-in-down me-2 fs-14"></i>
                                            {t('supplierRequests.view.tabComing')}
                                        </>
                                    }
                                >
                                    <TabComing
                                        canStartComing={canStartComing}
                                        showDraft={showDraft}
                                        draftComing={draftComing}
                                        setDraftComing={setDraftComing}
                                        isSubmitting={isSubmitting}
                                        canCredit={canCredit}
                                        pendingCloudFiles={pendingCloudFiles}
                                        setPendingCloudFiles={setPendingCloudFiles}
                                        cloudProgress={cloudProgress}
                                        getMaxCount={getMaxCount}
                                        onOpenDraft={handleOpenDraft}
                                        onCancelDraft={handleCancelDraft}
                                        onConduct={() => void handleConduct()}
                                        comings={request.comings || []}
                                    />
                                </Tab>
                            </Tabs>
                        </div>

                        <div className="p-3 offcanvas-footer off-coming-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    className="min100px"
                                    onClick={onHide}
                                    disabled={isSubmitting || cloudProgress !== false}
                                >
                                    {t('common:buttons.close')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffView;
